/**
 * Rewrite Builder CDN media on a page tree without re-registering the
 * built-in Image component (its editor schema is incompatible with a custom
 * override — see `hec/builder/index.ts`).
 *
 * URL rewrite only. Above-the-fold `highPriority` is COM-181
 * (`prioritizeAboveFoldImages`) — do not flag LCP here.
 *
 * - Image blocks: `image`/`src` get `format=webp` + a width, we emit a
 *   width-ladder `srcset` and set `noWebp` so the SDK does not prepend a
 *   second `format=webp`.
 * - Box / column `backgroundImage` (and kebab `background-image`) in
 *   responsive style bags: same CDN transform.
 *
 * Mutates in place. Safe as an SWR `transform` (same contract as
 * `slimBuilderEntry`).
 */
import {
  builderImageUrl,
  isBuilderImageUrl,
} from "~/hec/utils/builder-image-url";

/**
 * Fallback `src` width. The SDK generates its own
 * [100, 200, 400, 800, 1200, 1600, 2000] `w` ladder from this URL, so this is
 * the no-srcset-support floor, not what a modern browser downloads. Must stay
 * a bare `?width=` — see `rewriteImageOptions` on why `format` cannot ride
 * along.
 */
const IMAGE_FALLBACK_WIDTH = 1600;
const BG_WIDTH_LARGE = 1600;
const BG_WIDTH_MEDIUM = 1200;
const BG_WIDTH_SMALL = 800;

/**
 * The style keys that can carry a background image. Shared with the hero
 * background election in `builder-image-priority.ts`, which must read exactly
 * the declarations this module rewrites.
 */
export const BACKGROUND_STYLE_KEYS = [
  "backgroundImage",
  "background-image",
  "background",
] as const;

/**
 * An absolute `url(…)` inside a CSS value; group 2 is the URL. Global, so use
 * it with `replace` / `matchAll` (neither keeps `lastIndex` between calls) —
 * never with `exec` / `test`.
 */
export const CSS_IMAGE_URL = /url\(\s*(['"]?)(https?:\/\/[^)'"]+)\1\s*\)/gi;

export function rewriteBuilderMediaInPlace<T>(node: T): T {
  if (!node || typeof node !== "object") return node;
  visit(node);
  return node;
}

function visit(node: unknown): void {
  if (Array.isArray(node)) {
    for (const item of node) visit(item);
    return;
  }
  if (!node || typeof node !== "object") return;
  const obj = node as Record<string, unknown>;

  const component = obj.component;
  if (component && typeof component === "object") {
    const comp = component as Record<string, unknown>;
    if (
      comp.name === "Image" &&
      comp.options &&
      typeof comp.options === "object"
    ) {
      rewriteImageOptions(comp.options as Record<string, unknown>);
    }
  }

  const styles = obj.responsiveStyles;
  if (styles && typeof styles === "object") {
    const bag = styles as Record<string, unknown>;
    rewriteStyleBag(bag.large, BG_WIDTH_LARGE);
    rewriteStyleBag(bag.medium, BG_WIDTH_MEDIUM);
    rewriteStyleBag(bag.small, BG_WIDTH_SMALL);
    rewriteStyleBag(bag.xsmall, BG_WIDTH_SMALL);
  }
  rewriteStyleBag(obj.styles, BG_WIDTH_LARGE);
  rewriteStyleBag(obj.style, BG_WIDTH_LARGE);

  for (const key in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
    const val = obj[key];
    if (
      typeof val === "string" &&
      val.includes("url(") &&
      val.includes("cdn.builder.io")
    ) {
      obj[key] = rewriteCssImageUrls(val, BG_WIDTH_LARGE);
    } else {
      visit(val);
    }
  }
}

function rewriteImageOptions(options: Record<string, unknown>): void {
  const image = pickImageUrl(options.image) ?? pickImageUrl(options.src);
  if (!image || !isBuilderImageUrl(image)) return;

  const base = image.split("?")[0];
  // COM-189: `width` ONLY — deliberately no `format=webp` here.
  //
  // The SDK derives its srcset with `Number(url.split("?width=")[1])`. With
  // `?width=1600&format=webp` that parses as NaN, so the base candidate loses
  // its `w` descriptor and the srcset becomes an invalid mix of `Nw` entries
  // and one bare URL. `?width=1600` alone parses cleanly, and the SDK's own
  // webp `<source>` supplies the format — so webp is not lost, it moves to the
  // element that is actually allowed to negotiate it.
  options.image = builderImageUrl(base, { width: IMAGE_FALLBACK_WIDTH });
  if (typeof options.src === "string" && isBuilderImageUrl(options.src)) {
    options.src = builderImageUrl(options.src.split("?")[0], {
      width: IMAGE_FALLBACK_WIDTH,
    });
  }
  // COM-189: do NOT set `noWebp`, and do NOT hand the SDK a srcset.
  //
  // This previously set `options.srcset` and `options.noWebp = true`, on the
  // theory that we owned srcset and so could skip the SDK's double-`format=`
  // bug. Reading the shipped SDK image block, that combination emits NOTHING:
  //
  //   function i() {                                    // -> <img srcSet>
  //     const a = e.image || e.src;
  //     if (!a || !a.match(/builder\.io/)) return e.srcset;   // ours: only
  //                                                           // for non-CDN
  //     if (!e.noWebp) { ... return f(a); }             // SDK builds its own
  //     // noWebp === true -> NO return -> undefined
  //   }
  //   t() // -> <source type="image/webp">, requires !e.noWebp
  //
  // For a cdn.builder.io URL, `e.srcset` is unreachable: either `noWebp` is
  // false and the SDK regenerates from `f(a)` ignoring ours, or `noWebp` is
  // true and `i()` falls off the end returning undefined. So the flag intended
  // to give us srcset ownership is precisely what deleted the srcset — and it
  // also suppressed the webp `<source>`, since `t()` needs `!e.noWebp`.
  //
  // Measured consequence on the FSA homepage: every Builder image rendered as
  // a single `src?width=1600` with no candidates, so a 412px mobile viewport
  // downloaded the 1600px hero — 153 KB where the 412px candidate is 44 KB.
  //
  // Letting the SDK own both means `f(a)` emits the full
  // [100,200,400,800,1200,1600,2000] `w` srcset and `t()` emits the webp
  // `<source>`. `builderImageUrl` still collapses any duplicate `format=` the
  // SDK's `?` replacement introduces, which is what the old comment was
  // guarding against.
}

function pickImageUrl(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && "src" in value) {
    const src = (value as { src?: unknown }).src;
    if (typeof src === "string") return src;
  }
  return undefined;
}

function rewriteStyleBag(bag: unknown, width: number): void {
  if (!bag || typeof bag !== "object") return;
  const obj = bag as Record<string, unknown>;
  for (const key of BACKGROUND_STYLE_KEYS) {
    const value = obj[key];
    if (typeof value === "string") {
      obj[key] = rewriteCssImageUrls(value, width);
    }
  }
}

export function rewriteCssImageUrls(css: string, width: number): string {
  return css.replace(CSS_IMAGE_URL, (all, quote: string, url: string) => {
    if (!isBuilderImageUrl(url)) return all;
    return `url(${quote}${builderImageUrl(url, { width, format: "webp" })}${quote})`;
  });
}
